import * as Sentry from "@sentry/react";
import axios from "axios";
import { buildLoginApiUrl, getAuthHeaders } from "../../lib/config";
import type { ChangePasswordInput } from "../../lib/validations/auth/passwordValidation";
// Função para decodificar JWT (sem verificação de assinatura)
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Erro ao decodificar JWT:", error);
    return null;
  }
};

// Função para extrair dados do usuário do Access Token (ultra minimalista)
export const extractUserFromAccessToken = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Extrair dados básicos do Access Token
  const user = {
    id: decoded.sub,
    username: decoded.username,
    email: decoded.email,
    activeVinculoId: decoded.activeVinculoId,
    vinculos: decoded.vinculos || [],
  };

  return user;
};

// Função para extrair dados completos do usuário do ID Token
export const extractUserFromIdToken = (token: string) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;

  // Extrair dados completos do ID Token
  const user = {
    id: decoded.sub,
    username: decoded.username,
    email: decoded.email,
    pessoa: decoded.pessoa || null,
    activeVinculo: decoded.activeVinculo || null,
    vinculos: decoded.vinculos || [],
    isSuperAdmin: decoded.isSuperAdmin || false,
    confirmed: decoded.confirmed || false,
    blocked: decoded.blocked || false,
  };

  return user;
};

export const login = async (identifier: string, password: string) => {
  try {
    const response = await axios.post(buildLoginApiUrl("auth/login"), {
      identifier: identifier.trim(),
      password: password.trim(),
    });


    if (response.data.access_token) {
      // Extrair dados do usuário do ID Token (dados completos)
      const user = response.data.user;

      
      if (user) {
        // Armazenar ambos os tokens
        localStorage.setItem("access_token", response.data.access_token);
        Sentry.setUser({
          id: user.id,
          email: user.email,
          name: user.pessoa?.nome || user.username, // Safe access para pessoa
          username: user.username,
        });
        // localStorage.setItem("id_token", response.data.id_token);

        // Retornar dados estruturados
        return {
          user,
          access_token: response.data.access_token,
          id_token: response.data.id_token,
        };
      } else {
        throw new Error("Erro ao extrair dados do usuário do ID token");
      }
    } else {
      throw new Error("Resposta de login inválida: tokens não encontrados");
    }
  } catch (error: any) {
    console.error("❌ Erro no login:", error.response?.data || error.message);

    // Se for erro de rede ou servidor não disponível, mostrar erro específico
    if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("Network Error")
    ) {
      throw new Error(
        "Servidor não disponível. Verifique se a API está rodando em http://localhost:3008"
      );
    }

    // Se for erro de credenciais inválidas
    if (error.response?.status === 401) {
      throw new Error(
        "Credenciais inválidas. Verifique seu e-mail/usuário e senha."
      );
    }

    // Para outros erros, usar a mensagem da API ou erro genérico
    const errorMessage =
      error.response?.data?.message || error.message || "Erro ao fazer login";
    throw new Error(errorMessage);
  }
};

export const logout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("id_token");
  localStorage.removeItem("user");
};

export const getCurrentUser = () => {
  // Primeiro tentar obter do ID Token (dados completos)
  const idToken = localStorage.getItem("id_token");

  if (idToken) {
    const user = extractUserFromIdToken(idToken);
    if (user) {
      return user;
    }
  }

  // Fallback: tentar obter do localStorage (compatibilidade)
  const userStr = localStorage.getItem("user");
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (error) {
      console.error("Erro ao parsear usuário do localStorage:", error);
      localStorage.removeItem("user");
      return null;
    }
  }

  // Fallback: tentar extrair do Access Token (dados básicos)
  const accessToken = localStorage.getItem("access_token");
  if (accessToken) {
    const user = extractUserFromAccessToken(accessToken);
    if (user) {
      return user;
    }
  }

  return null;
};

export const getAccessToken = () => {
  return localStorage.getItem("access_token");
};

export const getIdToken = () => {
  return localStorage.getItem("id_token");
};

// Função de compatibilidade - mantém getToken() funcionando
export const getToken = () => {
  return getAccessToken();
};

// Helper: buscar permissões do usuário autenticado (REST cacheado no backend)
export const fetchUserPermissions = async () => {
  const token = getAccessToken();
  if (!token) return null;
  const url = buildLoginApiUrl("auth/me/permissions");
  try {
    const res = await fetch(url, { headers: getAuthHeaders(token) });
    if (!res.ok) return null;
    const data = await res.json();
    return data;
  } catch {
    return null;
  }
};

// Função para verificar se o token ainda é válido
export const validateToken = async (): Promise<boolean> => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return false;
  }

  try {
    // Fazer uma chamada leve para um endpoint protegido para validar o token
    await axios.get(buildLoginApiUrl("auth/me"), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    // Se a chamada for bem-sucedida, o token é válido
    return true;
  } catch (error) {
    console.error("Token inválido ou expirado, fazendo logout.", error);
    logout();
    return false;
  }
};

// Função para restaurar contexto ativo do JWT após refresh/reload
export const restoreActiveContextFromToken = async (userData: any) => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken || !userData) {
      return userData;
    }

    // Extrair informações do contexto ativo do access token
    const tokenData = extractUserFromAccessToken(accessToken);
    if (!tokenData?.activeVinculoId || !userData.vinculos) {
      return userData;
    }

    // Encontrar o vínculo ativo baseado no activeVinculoId do token
    const activeVinculo = userData.vinculos.find(
      (v: any) => v.id === tokenData.activeVinculoId
    );

    if (activeVinculo) {
      // Combinar dados do usuário com o contexto ativo do token
      return {
        ...userData,
        activeVinculo: activeVinculo,
        activeVinculoId: tokenData.activeVinculoId,
      };
    }

    return userData;
  } catch (error) {
    console.error('Erro ao restaurar contexto ativo do token:', error);
    return userData;
  }
};

// Função para obter dados completos do usuário via API
export const fetchUserCompleteData = async () => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) return null;

    const response = await axios.get(buildLoginApiUrl("auth/me"), {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    return response.data;
  } catch (error) {
    console.error("Erro ao buscar dados completos do usuário:", error);
    return null;
  }
};

// Função para trocar contexto (switch-context)
export const switchContext = async (vinculoId: number) => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error("Token não encontrado");
    }

    const response = await axios.post(
      buildLoginApiUrl("auth/switch-context"),
      {
        vinculoId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data.access_token) {
      // Atualizar tokens no localStorage
      localStorage.setItem("access_token", response.data.access_token);
      
      // Se temos id_token, atualizar também
      if (response.data.id_token) {
        localStorage.setItem("id_token", response.data.id_token);
        const user = extractUserFromIdToken(response.data.id_token);
        
        const result = {
          user,
          access_token: response.data.access_token,
          id_token: response.data.id_token,
        };
        return result;
      } else {
        // Usar dados do usuário retornados diretamente pela API
        // Extrair informações do contexto ativo do access token
        const tokenData = extractUserFromAccessToken(response.data.access_token);
        
        // Encontrar o vínculo ativo baseado no activeVinculoId do token
        let activeVinculo = null;
        if (tokenData?.activeVinculoId && response.data.user.vinculos) {
          activeVinculo = response.data.user.vinculos.find(
            (v: any) => v.id === tokenData.activeVinculoId
          );
        }
        
        // Combinar dados do usuário da API com o contexto ativo do token
        const updatedUser = {
          ...response.data.user,
          activeVinculo: activeVinculo,
          activeVinculoId: tokenData?.activeVinculoId,
        };
        
        const result = {
          user: updatedUser,
          access_token: response.data.access_token,
          id_token: null,
        };
        return result;
      }
    }

    throw new Error("Resposta de switch-context inválida");
  } catch (error: any) {
    console.error("Erro ao trocar contexto:", error.response?.data || error.message);
    throw new Error(
      error.response?.data?.message ||
        error.message ||
        "Erro ao trocar contexto"
    );
  }
};

// Interface para mudança de senha
// Removido - usando interface do arquivo de validação

// Função para alterar senha
export const changePassword = async (passwordData: ChangePasswordInput) => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) throw new Error("Token não encontrado");

    const response = await axios.post(
      buildLoginApiUrl("auth/change-password"),
      passwordData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("❌ Erro ao alterar senha:", error.response?.data || error.message);

    // Se for erro de rede ou servidor não disponível
    if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("Network Error")
    ) {
      throw new Error(
        "Servidor não disponível. Verifique se a API está rodando."
      );
    }

    // Se for erro 401 - senha atual incorreta
    if (error.response?.status === 401) {
      throw new Error("Senha atual incorreta");
    }

    // Se for erro 400 - validação
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || "Dados inválidos";
      throw new Error(errorMessage);
    }

    // Para outros erros, usar a mensagem da API ou erro genérico
    const errorMessage =
      error.response?.data?.message || error.message || "Erro ao alterar senha";
    throw new Error(errorMessage);
  }
};

// Interface para recuperação de senha
export interface ForgotPasswordInput {
  email: string;
}

// Interface para redefinição de senha
export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

// Função para solicitar recuperação de senha
export const forgotPassword = async (email: string) => {
  try {
    const response = await axios.post(
      buildLoginApiUrl("auth/forgot-password"),
      { email: email.trim() }
    );

    return response.data;
  } catch (error: any) {
    console.error("❌ Erro ao solicitar recuperação de senha:", error.response?.data || error.message);

    // Se for erro de rede ou servidor não disponível
    if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("Network Error")
    ) {
      throw new Error(
        "Servidor não disponível. Verifique se a API está rodando."
      );
    }

    // Se for erro 400 - validação
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || "E-mail inválido";
      throw new Error(errorMessage);
    }

    // Para outros erros, usar a mensagem da API ou erro genérico
    const errorMessage =
      error.response?.data?.message || error.message || "Erro ao solicitar recuperação de senha";
    throw new Error(errorMessage);
  }
};

// Função para redefinir senha com token
export const resetPassword = async (passwordData: ResetPasswordInput) => {
  try {
    const response = await axios.post(
      buildLoginApiUrl("auth/reset-password"),
      {
        token: passwordData.token,
        newPassword: passwordData.newPassword.trim(),
      }
    );

    return response.data;
  } catch (error: any) {
    console.error("❌ Erro ao redefinir senha:", error.response?.data || error.message);

    // Se for erro de rede ou servidor não disponível
    if (
      error.code === "ERR_NETWORK" ||
      error.message.includes("Network Error")
    ) {
      throw new Error(
        "Servidor não disponível. Verifique se a API está rodando."
      );
    }

    // Se for erro 400 - token inválido ou expirado
    if (error.response?.status === 400) {
      const errorMessage = error.response?.data?.message || "Token inválido ou expirado";
      throw new Error(errorMessage);
    }

    // Para outros erros, usar a mensagem da API ou erro genérico
    const errorMessage =
      error.response?.data?.message || error.message || "Erro ao redefinir senha";
    throw new Error(errorMessage);
  }
};
